/**
 * What a cell will not accept, decided before anything is sent.
 *
 * The alternative is letting the server refuse: the value is applied
 * optimistically, the request comes back 400, a toast appears and the cell
 * reverts. That is a round trip, a rollback and an error message for something
 * knowable from the characters typed -- and it loses the value rather than
 * leaving it there to be corrected.
 *
 * Pure, and answered from the *draft* rather than the parsed value, because a
 * draft that cannot be parsed at all is exactly the case worth reporting: every
 * `fromDraft` in this module answers `null` for text it cannot read, and `null`
 * is also what an emptied cell sends.
 *
 * The regular expressions are upstream's, which are themselves kept in step
 * with the backend's -- so a value this refuses is a value the backend would
 * have refused, and no more than that.
 */
import type { Field, FieldOf, FieldTypeName } from '@/api/types'
import BigNumber from 'bignumber.js'

import { durationFormatOf, parseDurationValue } from '../duration'
import { parseNumberValue } from '../numbers'

/**
 * A refusal, named by a translation key rather than by a sentence.
 *
 * This layer has no i18n and should not: it is the same pure module the MCP
 * apps and the node tests import.
 */
export interface CellValidationError {
  key: CellValidationKey
  values?: Record<string, string | number>
}

/** The names under `fieldErrors` in the translations, so a typo will not build. */
export type CellValidationKey =
  | 'invalidEmail'
  | 'max254Chars'
  | 'invalidUrl'
  | 'invalidPhoneNumber'
  | 'invalidNumber'
  | 'maxDigits'
  | 'invalidDuration'
  | 'overflowDuration'

type Validator = (field: Field, draft: string) => CellValidationError | null

/** The digits the backend stores, past which a number is not a number it has. */
const MAX_NUMBER_LENGTH = 50

/** The seconds a duration column holds, which is a `bigint` day count at heart. */
const MAX_DURATION_SECONDS = 86399999913600
const MIN_DURATION_SECONDS = -86399999913600

const URL_PATTERN = /^[^\s]{0,255}(?:\.|\/\/)[^\s]{1,}$/i

/**
 * Upstream's email pattern, rebuilt here rather than simplified.
 *
 * `\p{L}\p{M}*` is how a unicode letter is matched in JavaScript, which has no
 * unicode-aware `\w`; the backend uses Python's, which has. The lookahead is
 * what bounds the whole address at 254 characters.
 */
const EMAIL_PATTERN = new RegExp(
  '(?=^.{3,254}$)^([-.[\\]!#$&*+/=?^_`{|}~0-9]|\\p{L}\\p{M}*)+@([-.[\\]!#$&*+/=?^_`{|}~0-9]|\\p{L}\\p{M}*)+$',
  'iu',
)

const PHONE_PATTERN = /^[0-9NnXx,+._*()#=;/ -]{1,100}$/

const validateUrl: Validator = (_field, draft) =>
  URL_PATTERN.test(draft) ? null : { key: 'invalidUrl' }

const validateEmail: Validator = (_field, draft) => {
  if (draft.length > 254) return { key: 'max254Chars' }
  return EMAIL_PATTERN.test(draft) ? null : { key: 'invalidEmail' }
}

const validatePhoneNumber: Validator = (_field, draft) =>
  PHONE_PATTERN.test(draft) ? null : { key: 'invalidPhoneNumber' }

const validateNumber: Validator = (field, draft) => {
  // Read through the field's own formatting, so a currency prefix, a suffix and
  // the configured decimal mark are all read the way the editor writes them.
  // A raw `BigNumber(draft)` would refuse every value the cell itself produced.
  const parsed = parseNumberValue(field as FieldOf<'number'>, draft)
  if (parsed === null || parsed.isNaN() || !parsed.isFinite()) {
    return { key: 'invalidNumber' }
  }
  if (
    parsed
      .absoluteValue()
      .isGreaterThanOrEqualTo(new BigNumber(`10e${MAX_NUMBER_LENGTH}`))
  ) {
    return { key: 'maxDigits', values: { max: MAX_NUMBER_LENGTH } }
  }
  return null
}

const validateDuration: Validator = (field, draft) => {
  const seconds = parseDurationValue(draft, durationFormatOf(field))
  if (seconds === null) return { key: 'invalidDuration' }
  if (seconds > MAX_DURATION_SECONDS || seconds < MIN_DURATION_SECONDS) {
    return { key: 'overflowDuration' }
  }
  return null
}

/**
 * Nothing to refuse, and each `null` is a different reason.
 *
 * Most are read-only, so there is no draft at all. The rest are typed into
 * through a control that cannot produce a wrong value -- a rating is a row of
 * symbols and a password is set or cleared -- so a rule here would only ever
 * refuse something the interface could not have offered.
 */
const VALIDATORS = {
  url: validateUrl,
  email: validateEmail,
  phone_number: validatePhoneNumber,
  number: validateNumber,
  duration: validateDuration,

  text: null,
  long_text: null,
  rating: null,
  boolean: null,
  single_select: null,
  multiple_select: null,
  multiple_collaborators: null,
  date: null,
  link_row: null,
  file: null,
  password: null,

  formula: null,
  count: null,
  rollup: null,
  lookup: null,
  autonumber: null,
  uuid: null,
  created_on: null,
  created_by: null,
  last_modified: null,
  last_modified_by: null,
  form_view_edit_row: null,
} satisfies Record<FieldTypeName, Validator | null>

/**
 * What is wrong with what has been typed into a cell, or nothing.
 *
 * An empty draft is always accepted: it is how a cell is cleared, and refusing
 * it would make an emptied column unreachable.
 */
export function cellValidationError(
  field: Field,
  draft: string,
): CellValidationError | null {
  if (draft === '') return null
  const validate = VALIDATORS[field.type]
  return validate === null ? null : validate(field, draft)
}
